<template>
  <ion-header class="header">
    <ion-toolbar :class="{'transparent': isDesktop}">
      <div class="profile">
        <ion-text class="profile__title">{{ getReadableRole(user?.team?.types?.[0]?.name) }}</ion-text>
        <profile-user
          :avatar-src="user?.avatar_url"
          :team-name="user?.team?.company_name"
          :user-name="user?.name_first + ' ' + user?.name_last"
          title-id="User ID"
          :member-id="user?.team?.id"
          :user-id="user?.id"
          :user-rating="+user?.team?.score"
          :is-tracking="isAbleToDo(user, ProfileSettingsEnum.Tracking)"
          :has-unread-events="hasUnreadEvents"
          @on-navigate="navigate($event)"
          :hide-notice-button="!isActiveSubscriber"
        />
      </div>
    </ion-toolbar>
  </ion-header>
  <div v-if="isDesktop" :class="{'aside-menu': isDesktop}">
    <ion-button
      class="aside-menu__post-job"
      @click="openPostJob"
      :disabled="!isAbleToDo(user, ProfileSettingsEnum.PostWork) || !isActiveSubscriber"
    >
      Post a job
      <ion-icon slot="end" src="assets/icon/plus.svg" />
    </ion-button>
    <div
      v-for="item in appNav.filter(i => !i.isMainButton)"
      class="side-menu-link"
      :key="item.name"
      @click="isActiveSubscriber ? navigate(item) : null"
    >
      <ion-button
        fill="clear"
        class="side-menu-button"
        :disabled="!isActiveSubscriber"
      >
        <ion-icon v-if="route?.path?.includes(item?.url)" class="side-menu-button__dot" src="assets/icon/dot.svg" />
        <ion-icon class="side-menu-button__icon" :src="item.icon" />
        {{item.name}}
      </ion-button>
    </div>
  </div>
  <ion-content class="ion-padding" :class="{'transparent': isDesktop}">
    <div
      v-for="item in pagesNav"
      class="side-menu-link"
      :class="{'side-menu-link--disabled': user?.has_full_access ? false : item.route === RouteNamesEnum.Accounting}"
      :key="item.name"
      @click="navigate(item)"
    >
      <ion-button
        v-if="item.route"
        fill="clear"
        class="side-menu-button"
        :disabled="!isActiveSubscriber && !item?.isAlwaysVisible"
      >
        <ion-icon v-if="item?.urls?.some(item => route?.path.includes(item))" class="side-menu-button__dot" src="assets/icon/dot.svg" />
        <ion-icon class="side-menu-button__icon" :src="item.icon"/>
        {{ item.name }}
        <ion-img v-if="item.name === 'Messages' && hasUnseenMessages" src="assets/icon/red-dot.svg" class="red-dot" />
      </ion-button>
    </div>
  </ion-content>
  <div class="logo" v-if="isDesktop">
    <ion-img src="assets/icon/logo.svg"/>
  </div>
</template>

<script setup lang="ts">
import { IonButton, IonIcon, IonText, IonHeader, IonToolbar, IonContent, IonImg } from '@ionic/vue';
import { pagesNav } from '@/const/menu';
import ProfileUser from '@/modules/General/components/ProfileUser.vue';
import {useBreakpoint } from '@/hooks/useBreakpoint'
import { appNav } from '@/const/navigation';
import router from "@/router";
import {RouteNamesEnum} from "@/const/routes";
import { ProfileSettingsEnum, RegistrationProgress } from "@/gql/graphql";
import { MenuItem } from '@/interfaces/MenuItem';
import { MenuButton } from '@/interfaces/MenuButton';
import { isAbleToDo } from '@/helpers/isAbleToDo';
import { getReadableRole } from '@/helpers/companyRolesMap'
import { NavigationItem } from '@/interfaces/NavigationItem';
import { computed } from 'vue';
import { useUserStore } from '../store/userStore';
import { hasUnreadEvents } from "@/helpers/setUnreadEvents";
import { hasUnseenMessages } from "@/helpers/setUnseenMessages";
import { EventNamesEnum } from '@/const/events';
import { useRoute } from 'vue-router';

const user = computed(() => useUserStore()?.user);
const route = useRoute();

const isActiveSubscriber = computed(() => user.value?.team?.is_active_subscriber || (user.value?.is_impersonated && (user.value?.registration_progress === RegistrationProgress.Completed || user.value?.registration_progress === RegistrationProgress.Admin)));

const { isDesktop } = useBreakpoint();

const openPostJob = () => {
  router.push({
    name: RouteNamesEnum.PostJobChoose
  });
};

const emits = defineEmits<{
  (e: EventNamesEnum.OnNavigate, item: MenuItem | MenuButton | NavigationItem): void;
}>();

const navigate = (item: MenuItem | MenuButton | NavigationItem) => {
  if((isActiveSubscriber.value && user.value?.has_full_access) || item?.isAlwaysVisible || (isActiveSubscriber.value && !user.value?.has_full_access && item?.route !== RouteNamesEnum.Accounting)) {
    emits(EventNamesEnum.OnNavigate, item);
  }
}
</script>

<style lang="scss" scoped>
// ios + ionic top padding fix
ion-header ion-toolbar:first-of-type {
  padding-top: 0;
}

.header {
  width: auto;
  margin-left: 16px;
  margin-right: 16px;
  border-bottom: 2px solid var(--ion-color-light-grey);
  @media screen and (min-width: 1024px) {
    margin-left: 0;
    margin-right: 0;
    margin-bottom: 20px;
    border-bottom: 2px solid var(--ion-color-white);
  }
  &:after {
    display: none;
  }
}

.transparent {
  height: auto;
  contain: initial;
  --background: transparent;

  &::part(scroll){
    position: static;
    overflow: visible;
    padding: 0!important;
  }
}

.side-menu-link {
  display: block;
  margin-bottom: 10px;
  position: relative;
  cursor: pointer;

  @media screen and (min-width: 1024px) {
    &:hover {
      &:after {
        content: '';
        position: absolute;
        left: -40px;
        top: 0;
        right: -40px;
        bottom: 0;
        background: var(--ion-color-white);
        opacity: 0.15;
      }
    }
  }

  &--disabled {
    ion-button {
      color: var(--ion-color-light-grey);
    }
  }
}
.side-menu-button {
  margin: 0;
  height: auto;
  width: 100%;
  font-size: 16px;
  line-height: 1;
  font-weight: 500;
  letter-spacing: 0.3px;
  text-transform: none;
  --color: var(--ion-color-dark-grey);
  --padding-top: 0;
  --padding-start: 0;
  --padding-bottom: 0;
  --border-radius: 0;
  @media screen and (min-width: 1024px) {
    --padding-top: 5px;
    --padding-bottom: 5px;
    cursor: pointer;
    --color: var(--ion-color-white);
  }
  &::part(native) {
    width: auto;
  }

  .red-dot {
    margin-left: 8px;
  }

  .router-link-active & {
    padding-inline-start: 0;
    padding-inline-end: 0;
    --color: var(--ion-color-primary);
    
    @media screen and (min-width: 1024px) {
      --padding-start: 20px;
      position: relative;
      --color: var(--ion-color-white);

      &:before {
        content: '';
        position: absolute;
        left: 0;
        top: 50%;
        margin: -4px 0 0;
        width: 8px;
        height: 8px;
        border-radius: 50%;
        background: var(--ion-color-white);
      }
      &:after {
        content: '';
        position: absolute;
        left: -40px;
        top: 0;
        right: -40px;
        bottom: 0;
        background: var(--ion-color-white);
        opacity: 0.15;
      }
    }
  }

  &__icon {
    margin-right: 13px;
    display: flex;
    align-items: center;
    justify-content: center;

    :deep(.icon-inner) {
      width: auto;
      max-width: 100%;
    }
  }

  &__dot {
    margin-right: 13px;
    height: 8px;
    width: 8px;
  }
}

.profile {
  text-align: center;
  
  &__title {
    display: inline-block;
    vertical-align: top;
    margin-bottom: 16px;
    padding: 4px 10px;
    font-weight: 300;
    font-size: 14px;
    line-height: 16px;
    color: var(--ion-color-white);
    background: var(--ion-color-primary);
    border-radius: 0 0 4px 4px;
    @media screen and (min-width: 1024px) {
      padding: 4px 5px;
      margin-bottom: 20px;
      font-weight: 500;
      background: var(--ion-color-white);
      color: var(--ion-color-primary);
    }
  }

  .user-profile__content {
    padding-bottom: 15px;
    @media screen and (min-width: 1024px) {
      padding-bottom: 8px;
    }
  }
}

.aside-menu {
  margin-bottom: 15px;
  padding-bottom: 5px;
  border-bottom: 2px solid var(--ion-color-white);

  &__post-job {
    display: block;
    text-transform: uppercase;
    margin: 0 0 12px;
    height: auto;
    font-weight: 500;
    font-size: 14px;
    line-height: 1;
    letter-spacing: 0.3px;
    --border-width: 1px;
    --padding-top: 9px;
    --padding-bottom: 9px;
    --border-radius: 7px;
    --background: var(--ion-color-white);
    --color:var(--ion-color-primary);
    --box-shadow: none;
    ion-icon {
      font-size: 24px;
      margin: 0 0 0 19px;
    }
  }

  a {
    display: block;
    margin-bottom: 7px;
    position: relative;
    @media screen and (min-width: 1024px) {
      &:hover {
        &:after {
          content: '';
          position: absolute;
          left: -40px;
          top: 0;
          right: -40px;
          bottom: 0;
          background: var(--ion-color-white);
          opacity: 0.15;
        }
      }
    }
    .button {
      height: auto;
      font-size: 16px;
      text-transform: capitalize;
      --color:var(--ion-color-white);
      --padding-start: 0;
      --padding-top: 9px;
      --padding-bottom: 9px;
      ion-icon {
        margin-right: 12px;
        font-size: 19px;
      }
    }

  }
}

.logo {
  width: 78px;
  margin: 35px auto 20px;
}

:deep(.profile) {
  .user-profile {
    &__image {
      @media screen and (min-width: 1024px) {
        border-color: var(--ion-color-success);
        flex: 0 0 80px;
      }
    }
    &__team-name {
      @media screen and (min-width: 1024px) {
        color: var(--ion-color-white);
      }
    }
    &__name {
      @media screen and (min-width: 1024px) {
        color: var(--ion-color-white);
      }
    }

    &__id {
      @media screen and (min-width: 1024px) {
        color: var(--ion-color-white);
      }
    }

    &__rating {
      @media screen and (min-width: 1024px) {
        --star-color: var(--ion-color-white);
      }
    }

    &__button {
      @media screen and (min-width: 1024px) {
        --color: var(--ion-color-white);
      }
      span {
        @media screen and (min-width: 1024px) {
          border-color: var(--ion-color-white);
        }
      }
    }
  }
}
:deep(.star-rating__title) {
  @media screen and (min-width: 1024px) {
    color: var(--ion-color-white);
  }
}
</style>
